import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, Clock, CheckCircle2, Circle, MessageSquare, ShieldCheck, ExternalLink, Copy, Inbox,
} from "lucide-react";
import api, { API, getBoardroomToken } from "../utils/qccaApi";

export default function QCCAVoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [showOutbox, setShowOutbox] = useState(false);
  const [loading, setLoading] = useState(true);

  const publicUrl = `${window.location.origin}/results/${id}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/qcca/votes/${id}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) nav("/qcca/boardroom/login");
      else if (e?.response?.status === 404) { toast.error("Vote not found."); nav("/qcca/boardroom/votes"); }
      else toast.error("Could not load vote.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!getBoardroomToken()) { nav("/qcca/boardroom/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOutbox = async () => {
    try {
      const r = await api.get(`/qcca/votes/${id}/outbox`);
      setOutbox(r.data.emails || []);
      setShowOutbox(true);
    } catch { toast.error("Could not load outbox."); }
  };

  if (loading || !data) {
    return <div className="min-h-screen bg-black text-gray-500 font-mono text-sm p-10">Loading…</div>;
  }

  const { vote, tally, ballots } = data;
  const yesPct = tally.total_cast ? Math.round((tally.yes / tally.total_cast) * 100) : 0;
  const noPct = tally.total_cast ? Math.round((tally.no / tally.total_cast) * 100) : 0;
  const abstainPct = tally.total_cast ? 100 - yesPct - noPct : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom/votes" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Votes
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className={`font-mono text-[9px] uppercase tracking-[0.22em] px-2 py-1 border ${
              vote.status === "open" ? "border-[#C5A52A] text-[#C5A52A] bg-[#C5A52A]/5" : "border-[#1a1a1a] text-gray-500"
            }`}>{vote.status}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 inline-flex items-center gap-1">
              <Clock className="w-3 h-3 text-[#C5A52A]" /> Deadline {new Date(vote.deadline_iso).toLocaleString()}
            </span>
          </div>
          <h1 className="font-headline text-4xl lg:text-5xl text-white leading-tight" data-testid="qcca-vote-title">{vote.title}</h1>
          <p className="mt-4 text-gray-400 max-w-3xl leading-relaxed whitespace-pre-wrap">{vote.description}</p>
        </div>

        {/* Tally */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 bg-[#0a0a0a] border border-[#1a1a1a] p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Live tally</div>
            <div className="mt-6 space-y-5" data-testid="qcca-vote-tally">
              <Bar label="Yes" value={tally.yes} pct={yesPct} color="#C5A52A" />
              <Bar label="No" value={tally.no} pct={noPct} color="#7a7a7a" />
              <Bar label="Abstain" value={tally.abstain} pct={abstainPct} color="#3a3a3a" />
            </div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Turnout</div>
            <div className="mt-4 font-headline text-6xl text-white">{tally.turnout_pct}%</div>
            <div className="mt-1 font-mono text-[11px] text-gray-500">{tally.total_cast} of {tally.total_voters} ballots cast</div>
            <div className="mt-6 h-px bg-gradient-to-r from-transparent via-[#C5A52A] to-transparent" />
            <button
              onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Public results link copied."); }}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-vote-copy-public">
              <Copy className="w-3 h-3" /> Copy public results URL
            </button>
            <button onClick={loadOutbox}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-vote-view-outbox">
              <Inbox className="w-3 h-3" /> View mocked outbox
            </button>
          </div>
        </div>

        {/* Mocked outbox */}
        {showOutbox && (
          <div className="mb-10 bg-[#0a0a0a] border border-[#C5A52A]/40 p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">MOCKED outbox — local preview only</div>
                <p className="mt-2 text-gray-400 text-sm">These are the emails Resend would dispatch. Click any ballot link to cast a test vote.</p>
              </div>
              <button onClick={() => setShowOutbox(false)} className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-white">Hide</button>
            </div>
            <div className="mt-5 divide-y divide-[#1a1a1a] border-y border-[#1a1a1a]" data-testid="qcca-vote-outbox">
              {outbox.map((m) => (
                <div key={m.id} className="py-3 grid md:grid-cols-12 gap-3 items-center text-sm">
                  <div className="md:col-span-4 font-mono text-xs text-gray-300">Unit {m.unit} · {m.to_email}</div>
                  <div className="md:col-span-6 font-mono text-xs text-gray-500 truncate">{m.subject}</div>
                  <div className="md:col-span-2 text-right">
                    <a href={m.ballot_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 border border-[#1a1a1a] hover:border-[#C5A52A] text-[#C5A52A] font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1.5 transition-colors">
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ballot list (board-only) */}
        <div className="bg-[#0a0a0a] border border-[#1a1a1a]">
          <div className="p-6 border-b border-[#1a1a1a] flex items-center gap-3">
            <ShieldCheck className="w-4 h-4 text-[#C5A52A]" />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400">
              Board-only roster · {ballots.length} ballot{ballots.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="divide-y divide-[#1a1a1a]" data-testid="qcca-vote-ballots">
            {ballots.map((b) => {
              const voted = b.voted_at;
              return (
                <div key={b.id} className="p-5 grid md:grid-cols-12 gap-3 items-start">
                  <div className="md:col-span-3 font-mono text-xs">
                    <div className="text-[#C5A52A]">Unit {b.unit}</div>
                    <div className="text-gray-500 mt-0.5">{b.name || "—"}</div>
                  </div>
                  <div className="md:col-span-3 font-mono text-xs text-gray-500 truncate">{b.email}</div>
                  <div className="md:col-span-2 font-mono text-[11px]">
                    {voted ? (
                      <span className="inline-flex items-center gap-1 text-white">
                        <CheckCircle2 className="w-3 h-3 text-[#C5A52A]" /> {b.choice?.toUpperCase()}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-600">
                        <Circle className="w-3 h-3" /> not voted
                      </span>
                    )}
                  </div>
                  <div className="md:col-span-2 font-mono text-[10px] text-gray-500">
                    {voted ? new Date(voted).toLocaleString() : "—"}
                  </div>
                  <div className="md:col-span-2 font-mono text-[11px] text-gray-400 flex items-start gap-1">
                    {b.comment && (
                      <>
                        <MessageSquare className="w-3 h-3 text-[#C5A52A] shrink-0 mt-0.5" />
                        <span className="italic">"{b.comment}"</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Audit log */}
        <div className="mt-10 bg-[#050505] border border-[#1a1a1a] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Transparent audit log</div>
          <p className="mt-2 text-gray-500 text-xs">Timestamps + choices only — no names, no unit numbers. This is what appears on the public results page at <span className="text-gray-300">/results/{id}</span>.</p>
          <div className="mt-4 font-mono text-[11px] text-gray-400 max-h-60 overflow-y-auto">
            {ballots.filter((b) => b.voted_at).length === 0 ? (
              <div className="text-gray-600">No ballots cast yet.</div>
            ) : ballots.filter((b) => b.voted_at).sort((a, b) => a.voted_at.localeCompare(b.voted_at)).map((b, i) => (
              <div key={b.id} className="py-1 grid grid-cols-12 gap-2">
                <span className="col-span-1 text-gray-600">{String(i + 1).padStart(3, "0")}</span>
                <span className="col-span-5">{new Date(b.voted_at).toISOString()}</span>
                <span className={`col-span-2 ${b.choice === "yes" ? "text-[#C5A52A]" : b.choice === "no" ? "text-gray-300" : "text-gray-500"}`}>
                  {b.choice?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Bar({ label, value, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between font-mono text-xs mb-1">
        <span className="text-gray-300 uppercase tracking-[0.18em]">{label}</span>
        <span className="text-gray-500">{value} · {pct}%</span>
      </div>
      <div className="h-3 bg-black border border-[#1a1a1a]">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
